import {
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import CloseIcon from "@material-ui/icons/Close"
import EditIcon from "@material-ui/icons/Edit"
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined"
import clsx from "clsx"
import { useContext, useEffect, useMemo, useState } from "react"
import { ChromePicker } from "react-color"
import { useQuery, useQueryClient } from "react-query"
import { useDebounce } from "use-lodash-debounce"
import { listClients } from "~/actions/clients"
import CounterHeader from "~/components/Common/CounterHeader"
import ThumbnailUpload from "~/components/Common/ThumbnailUpload"
import AppContext from "~/contexts/App"
import CategoryContext from "~/contexts/Category"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"

import Box from "../../Common/Box"
import useStyles from "./styles"

const CategoryDetails = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const openErrorSnackbar = useOpenErrorSnackbar()
  const { setPageSubTitle } = useContext(AppContext)
  const { data: clients } = useQuery("clients", () => listClients())

  const {
    category,
    setInitialDetailsContext,
    debouncedDetails,
    setDebouncedDetails,
    catDetailsValues,
    setCatDetailsValues,
    updateCategoryMutation,
    tryToSave,
    categoryCourses,
    categoryLearningPaths,
    categoryArticles,
  } = useContext(CategoryContext)

  const { refetchCategory } = useContext(CategoryContext)

  useEffect(() => {
    if (category?.id) {
      setCatDetailsValues({
        name: category.name,
        description: category.description || "",
        headline: category.headline || "",
        type: category.type || "topic",
        published_state: category.published_state || "draft",
        partner_status: category.partner_status || null,
        link: category.link || null,
        image: category.image || "",
        image_color: category.image_color || "",
        hex_inner: category.hex_inner || "",
        hex_outer: category.hex_outer || "",
        client_id: category.client_id || null,
        channel_details: {
          channel_card: category?.channel_details?.channel_card || "",
          banner: {
            title: category?.channel_details?.banner?.title || "",
            image: category?.channel_details?.banner?.image || "",
          },
          hero: {
            title: category?.channel_details?.hero?.title || "",
            description: category?.channel_details?.hero?.description || "",
            type: category?.channel_details?.hero?.type || "",
            videoSrc: category?.channel_details?.hero?.videoSrc || "",
          },
          overview: {
            description: category?.channel_details?.overview?.description || "",
            iconList: category?.channel_details?.overview?.iconList || [],
          },
        },
        // internal_message: category.internal_message || null,
      })
      setInitialDetailsContext({
        name: category.name,
        description: category.description || "",
        headline: category.headline || "",
        type: category.type || "topic",
        published_state: category.published_state || "draft",
        partner_status: category.partner_status || null,
        link: category.link || null,
        image: category.image || "",
        image_color: category.image_color || "",
        hex_inner: category.hex_inner || "",
        hex_outer: category.hex_outer || "",
        client_id: category.client_id || null,
        channel_details: {
          channel_card: category?.channel_details?.channel_card || "",
          banner: {
            title: category?.channel_details?.banner?.title || "",
            image: category?.channel_details?.banner?.image || "",
          },
          hero: {
            title: category?.channel_details?.hero?.title || "",
            description: category?.channel_details?.hero?.description || "",
            type: category?.channel_details?.hero?.type || "",
            videoSrc: category?.channel_details?.hero?.videoSrc || "",
          },
          overview: {
            description: category?.channel_details?.overview?.description || "",
            iconList: category?.channel_details?.overview?.iconList || [],
          },
        },
        // internal_message: category.internal_message || null,
      })
    }
  }, [category])

  const debouncedLocalValues = useDebounce(catDetailsValues, 200)
  const [showPickerInner, setShowPickerInner] = useState(false)
  const [showPickerOuter, setShowPickerOuter] = useState(false)

  const theme = useTheme()
  const phoneView = useMediaQuery(theme.breakpoints.down("sm"))

  useEffect(() => {
    setDebouncedDetails(catDetailsValues)
  }, [debouncedLocalValues])

  const descriptionError = debouncedDetails?.description?.length > 2000

  const linkError =
    (debouncedDetails.type === "partner" &&
      debouncedDetails.partner_status !== "internal" &&
      (debouncedDetails.link === null || debouncedDetails.link.length === 0)) ||
    debouncedDetails?.link?.length > 100

  // const internalMessageError = debouncedDetails?.internal_message?.length > 1000

  const partnerStatusError =
    debouncedDetails.type === "partner" &&
    debouncedDetails?.partner_status === null

  const nameError =
    debouncedDetails?.name === "" || debouncedDetails?.name?.length > 50

  useEffect(() => {
    setPageSubTitle("")
  }, [])

  const setThumbnail = (uploadRes, loadingAction, type) => {
    const image =
      uploadRes === null
        ? null
        : {
            url: uploadRes.data.url,
            secure_url: uploadRes.data.secure_url,
            original_filename: uploadRes.data.original_filename,
            version: uploadRes.data.version,
            format: uploadRes.data.format,
            public_id: uploadRes.data.public_id,
          }

    const orderedCourses = categoryCourses.map((c, i) => ({
      course_id: c.id,
      course_container_id: c.container_id,
      order: i,
    }))
    const orderedLearningPaths = categoryLearningPaths.map((lp, i) => ({
      learning_path_id: lp.id,
      order: i,
    }))
    const orderedDocs = categoryArticles
      .filter((x) => x?.type === "doc")
      .map((y, i) => {
        return {
          article_id: y?.id,
          order: i,
        }
      })
    const orderedUpdates = categoryArticles
      .filter((x) => x?.type === "update")
      .map((y, i) => {
        return {
          article_id: y?.id,
          order: i,
        }
      })

    const orderedArticles = orderedDocs.concat(orderedUpdates)
    const data = {
      ...debouncedDetails,
      articles: orderedArticles,
      courses: orderedCourses,
      learning_paths: orderedLearningPaths,
      [type]: image === null ? null : JSON.stringify(image),
    }
    updateCategoryMutation.mutate(data, {
      onSuccess: (res) => {
        serialize("category", res.data).then((serializedData) => {
          return queryClient.setQueryData("categories", (oldData) => {
            let newArray = []
            newArray = oldData.map((x) => {
              if (x.id !== serializedData.id) {
                return x
              } else {
                x[type] = serializedData[type]
                return x
              }
            })
            return [...newArray]
          })
          // return
        })
        refetchCategory()
        loadingAction(false)
      },
      onError: (err) => {
        console.error(err)
        loadingAction(false)
        openErrorSnackbar(err, "Error Updating Thumbnail")
      },
    })
  }
  const innerColor = useMemo(() => {
    let holder
    if (catDetailsValues?.hex_inner) {
      if (catDetailsValues.hex_inner.length > 0) {
        holder = catDetailsValues.hex_inner
      } else {
        holder = "#000"
      }
    } else {
      holder = "#000"
    }
    return holder
  }, [catDetailsValues.hex_inner])

  const outerColor = useMemo(() => {
    let holder
    if (catDetailsValues?.hex_outer) {
      if (catDetailsValues.hex_outer.length > 0) {
        holder = catDetailsValues.hex_outer
      } else {
        holder = "#000"
      }
    } else {
      holder = "#000"
    }
    return holder
  }, [catDetailsValues.hex_outer])

  const handleChangeClient = (event) => {
    setCatDetailsValues((oldState) => ({
      ...oldState,
      client_id: event.target.value,
    }))
  }

  return (
    <Box className={clsx(classes.container, "overflow-scroll h-full pb-32")}>
      <Typography variant="h3" color="primary" className={classes.title}>
        Category Details
      </Typography>
      <div className={classes.twoFieldContainer}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Category Name"
            value={debouncedDetails?.name}
            max={50}
          />
          <TextField
            value={catDetailsValues?.name || ""}
            onChange={(e) => {
              setCatDetailsValues((oldState) => ({
                ...oldState,
                name: e.target.value,
              }))
            }}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
          {nameError ? (
            <Typography className={classes.requiredText}>
              Name is required and it has to be less than 50 characters
            </Typography>
          ) : null}
        </Box>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Typography color="secondary" className={classes.inputLabel}>
            Headline
          </Typography>
          <TextField
            value={catDetailsValues?.headline || ""}
            onChange={(e) => {
              setCatDetailsValues((oldState) => ({
                ...oldState,
                headline: e.target.value,
              }))
            }}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
        </Box>
      </div>
      {/*//? ---------------------------------------------------- */}
      <Box className={classes.twoFieldContainer} marginTop="8px">
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Typography
            variant="h3"
            color="primary"
            className={classes.inputLabel}
          >
            Published State
          </Typography>
          <RadioGroup
            className="flex flex-row gap-4 mb-3"
            value={
              catDetailsValues?.published_state
                ? catDetailsValues.published_state
                : ""
            }
            onChange={(e) => {
              setCatDetailsValues((oldState) => ({
                ...oldState,
                published_state: e.target.value,
              }))
              return
            }}
          >
            <Box key={catDetailsValues?.published_state}>
              <FormControlLabel
                label="Draft"
                value="draft"
                control={
                  <Radio
                    value="draft"
                    checked={catDetailsValues?.published_state === "draft"}
                  />
                }
              />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                label="Published"
                value="published"
                control={
                  <Radio
                    value="published"
                    checked={catDetailsValues?.published_state === "published"}
                  />
                }
              />
            </Box>
          </RadioGroup>
        </Box>
        {/*//? ---------------------------------------------------- */}
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Typography
            variant="h3"
            color="primary"
            className={classes.inputLabel}
          >
            Category Type
          </Typography>
          <RadioGroup
            className="flex flex-row gap-4 mb-3"
            value={catDetailsValues?.type ? catDetailsValues.type : " "}
            onChange={(e) => {
              setCatDetailsValues((oldState) => ({
                ...oldState,
                type: e.target.value,
              }))
              e.target.value === "topic" &&
                setCatDetailsValues((oldState) => ({
                  ...oldState,
                  partner_status: null,
                  link: null,
                  // internal_message: null,
                }))
              return
            }}
          >
            <Box key={catDetailsValues?.type}>
              <FormControlLabel
                label="Topic"
                value="topic"
                control={
                  <Radio
                    value="topic"
                    checked={catDetailsValues?.type === "topic"}
                  />
                }
              />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                label="Partner"
                value="partner"
                control={
                  <Radio
                    value="partner"
                    checked={catDetailsValues?.type === "partner"}
                  />
                }
              />
            </Box>
          </RadioGroup>
        </Box>
      </Box>
      <Box className={classes.twoFieldContainer} marginTop="8px">
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Typography
            variant="h3"
            color="primary"
            className={clsx(
              classes.inputLabel,
              catDetailsValues.type === "partner"
                ? "opacity-100"
                : "opacity-40",
            )}
          >
            Partner Status
          </Typography>
          <RadioGroup
            className={clsx(
              "flex flex-row gap-4 mb-3",
              catDetailsValues.type === "partner"
                ? "opacity-100"
                : "opacity-40 pointer-events-none",
            )}
            value={
              catDetailsValues?.partner_status
                ? catDetailsValues.partner_status
                : null
            }
            required={true}
            onChange={(e) => {
              catDetailsValues.type === "partner" &&
                setCatDetailsValues((oldState) => ({
                  ...oldState,
                  partner_status: e.target.value,
                }))
              // e.target.value === "external" &&
              //   setCatDetailsValues((oldState) => ({
              //     ...oldState,
              //     internal_message: null,
              //   }))
              e.target.value === "internal" &&
                setCatDetailsValues((oldState) => ({
                  ...oldState,
                  link: null,
                }))
              return
            }}
          >
            <Box key={catDetailsValues?.partner_status}>
              <FormControlLabel
                label="Internal"
                value="internal"
                control={
                  <Radio
                    value="internal"
                    checked={catDetailsValues?.partner_status === "internal"}
                  />
                }
              />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                label="External"
                value="external"
                control={
                  <Radio
                    value="external"
                    checked={catDetailsValues?.partner_status === "external"}
                  />
                }
              />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                label="Both"
                value="both"
                control={
                  <Radio
                    value="both"
                    checked={catDetailsValues?.partner_status === "both"}
                  />
                }
              />
            </Box>
          </RadioGroup>
          {partnerStatusError && tryToSave && (
            <Typography className={classes.requiredText}>
              Partner Status must be either internal, external, or both
            </Typography>
          )}
        </Box>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <div
            className={clsx(
              "mt-2",
              catDetailsValues?.type === "partner" &&
                catDetailsValues.partner_status !== "internal"
                ? "opacity-100"
                : "opacity-40 pointer-events-none",
            )}
          >
            <CounterHeader
              title="Link (ex. /channels/meta)"
              value={debouncedDetails?.link || ""}
              max={100}
            />
            <TextField
              value={catDetailsValues?.link || ""}
              multiline
              maxRows={1}
              onChange={(e) => {
                setCatDetailsValues((oldState) => ({
                  ...oldState,
                  link: e.target.value,
                }))
              }}
              InputProps={{
                disableUnderline: true,
                classes: {
                  root: classes.inputDescription,
                },
              }}
              size="small"
              fullWidth
            />
            {linkError && tryToSave ? (
              <Typography className={classes.requiredText}>
                Link is required
              </Typography>
            ) : null}
          </div>
        </Box>
      </Box>
      <div
        className={clsx(
          "mt-2",
          catDetailsValues?.type === "partner" &&
            (catDetailsValues?.partner_status === "internal" ||
              catDetailsValues?.partner_status === "both")
            ? "opacity-100"
            : "opacity-40 pointer-events-none",
        )}
      ></div>
      <div className="my-2">
        <CounterHeader
          title="Category Description"
          value={debouncedDetails?.description}
          max={2000}
        />
        <TextField
          value={catDetailsValues.description || ""}
          multiline
          maxRows={10}
          onChange={(e) => {
            setCatDetailsValues((oldState) => ({
              ...oldState,
              description: e.target.value,
            }))
          }}
          InputProps={{
            disableUnderline: true,
            classes: {
              root: classes.inputDescription,
            },
          }}
          size="small"
          fullWidth
        />
        {descriptionError ? (
          <Typography className={classes.requiredText}>
            Description has to be less than 2000 characters
          </Typography>
        ) : null}
      </div>

      {/* <Box display="flex" justifyContent="space-between" mb={1} m1={2}> */}
      <Box className={classes.thumbnailSectionContainer}>
        <Box className={classes.thumbnailContainer}>
          <Typography color="secondary" className={classes.inputLabel}>
            Partner Logo
            <Tooltip title="Logo on popular channel card">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <div className="bg-gray-300">
            <ThumbnailUpload
              title="Drop image here"
              cloudinaryUploadPreset={
                import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
              }
              value={category?.image ? JSON.parse(category.image) : ""}
              onUpload={setThumbnail}
              noCrop={true}
              type="image"
            />
          </div>
          {Boolean(category?.image) && (
            <Typography variant="h4" className={classes.mediaKey}>
              {JSON.parse(category.image)?.original_filename}.
              {JSON.parse(category.image)?.format}
            </Typography>
          )}
        </Box>

        <Box className={classes.thumbnailContainer}>
          <Typography color="secondary" className={classes.inputLabel}>
            Partner Logo (Color)
            <Tooltip title="Logo on popular channel card">
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <div className="bg-gray-300">
            <ThumbnailUpload
              title="Drop image here"
              cloudinaryUploadPreset={
                import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
              }
              value={
                category?.image_color ? JSON.parse(category.image_color) : ""
              }
              onUpload={setThumbnail}
              noCrop={true}
              type="image_color"
            />
          </div>
          {Boolean(category?.image_color) && (
            <Typography variant="h4" className={classes.mediaKey}>
              {JSON.parse(category.image_color)?.original_filename}.
              {JSON.parse(category.image_color)?.format}
            </Typography>
          )}
        </Box>
      </Box>
      <Box className={classes.thumbnailContainer}>
        <Typography color="secondary" className={classes.inputLabel}>
          Card Logo
        </Typography>
        <>
          <div className="bg-gray-300">
            <ThumbnailUpload
              title="Drop small image here"
              cloudinaryUploadPreset={
                import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
              }
              value={
                category?.card_image ? JSON.parse(category.card_image) : ""
              }
              onUpload={setThumbnail}
              noCrop={true}
              type="card_image"
            />
          </div>
          {Boolean(category?.card_image) && (
            <Typography variant="h4" className={classes.mediaKey}>
              {JSON.parse(category.card_image)?.original_filename}.
              {JSON.parse(category.card_image)?.format}
            </Typography>
          )}
        </>
      </Box>
      {/* </Box> */}
      {/* <Box width="100%" className={phoneView ? "block" : "hidden"}>
        <Typography color="secondary" className={classes.inputLabel}>
          Partner Logo (Color)
          <Tooltip title="Logo on popular channel card">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Typography>
        <div className="bg-gray-300">
          <ThumbnailUpload
            title="Drop image here"
            cloudinaryUploadPreset={
              import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
            }
            value={
              category?.image_color ? JSON.parse(category.image_color) : ""
            }
            onUpload={setThumbnail}
            noCrop={true}
            type="image_color"
          />
        </div>
        {Boolean(category?.image_color) && (
          <Typography variant="h4" className={classes.mediaKey}>
            {JSON.parse(category.image_color)?.original_filename}.
            {JSON.parse(category.image_color)?.format}
          </Typography>
        )}
      </Box> */}
      <Box display="flex" justifyContent="space-between" marginBottom="8px">
        <div className={phoneView ? "100%" : "50%"}>
          <Box width="100%" position="relative">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography color="secondary" className={classes.inputLabel}>
                Popular Channel Card Inner Hex
              </Typography>
              <IconButton
                onClick={() => setShowPickerInner((oldState) => !oldState)}
              >
                {!showPickerInner ? (
                  <EditIcon fontSize="small" />
                ) : (
                  <CloseIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
            <TextField
              disabled
              value={catDetailsValues?.hex_inner || ""}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
                endAdornment: (
                  <Box
                    borderRadius="50%"
                    width="15px"
                    height="15px"
                    style={{ backgroundColor: catDetailsValues?.hex_inner }}
                  />
                ),
              }}
              variant="filled"
              size="small"
              fullWidth
            />
            {showPickerInner && (
              <Box
                position="absolute"
                right={0}
                top={50}
                style={{ zIndex: 50 }}
              >
                <ChromePicker
                  disableAlpha
                  color={catDetailsValues?.hex_inner}
                  onChange={(value) => {
                    setCatDetailsValues((oldState) => ({
                      ...oldState,
                      hex_inner: value?.hex,
                    }))
                  }}
                />
              </Box>
            )}
          </Box>

          <Box width="100%" position="relative">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography color="secondary" className={classes.inputLabel}>
                Popular Channel Card Outer Hex
              </Typography>
              <IconButton
                onClick={() => setShowPickerOuter((oldState) => !oldState)}
              >
                {!showPickerOuter ? (
                  <EditIcon fontSize="small" />
                ) : (
                  <CloseIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
            <TextField
              disabled
              value={catDetailsValues?.hex_outer || ""}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
                endAdornment: (
                  <Box
                    borderRadius="50%"
                    width="15px"
                    height="15px"
                    style={{ backgroundColor: catDetailsValues?.hex_outer }}
                  />
                ),
              }}
              variant="filled"
              size="small"
              fullWidth
            />
            {showPickerOuter && (
              <Box
                position="absolute"
                right={0}
                top={50}
                style={{ zIndex: 50 }}
              >
                <ChromePicker
                  disableAlpha
                  color={catDetailsValues?.hex_outer}
                  onChange={(value) => {
                    setCatDetailsValues((oldState) => ({
                      ...oldState,
                      hex_outer: value?.hex,
                    }))
                  }}
                />
              </Box>
            )}
          </Box>
        </div>
        <Box width={phoneView ? "100%" : "48%"} position="relative">
          <Typography color="secondary" className={classes.inputLabel}>
            Popular Channel Card Preview
          </Typography>
          <div
            className="w-full rounded-lg transform group transition-all duration-200 flex items-center justify-center hover:ring-1 hover:ring-offset-0 ring-offset-transparent hover:ring-[#9ca1ab] focus-visible:ring-1 focus-visible:ring-[#9ca1ab]  outline-none h-full"
            style={{
              background: `radial-gradient( ${innerColor}, ${outerColor})`,
            }}
            draggable={false}
          >
            {catDetailsValues?.image_color && (
              <img
                src={JSON.parse(catDetailsValues?.image_color).url}
                alt="alt text"
                className={clsx(
                  "absolute top-1/2 transform -translate-y-1/2 object-cover scale-100 group-hover:scale-105 focus-visible:scale-105 transition-transform duration-200 outline-none h-8",
                )}
                draggable={false}
              />
            )}
          </div>
        </Box>
      </Box>
      <div className="nd:w-[50%] mt-8">
        <Typography color="secondary" className={classes.inputLabel}>
          Client
        </Typography>
        <FormControl variant="outlined" className={classes.selectContainer}>
          <Select
            value={catDetailsValues?.client_id || null}
            onChange={handleChangeClient}
            labelId="demo-simple-select-outlined-label"
            id="demo-simple-select-outlined"
          >
            <MenuItem value={null}>No Client</MenuItem>
            {clients?.map((c) => {
              return (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
      </div>
    </Box>
  )
}

export default CategoryDetails
